import {useEffect, useState} from 'react'
import _ from 'lodash'
// Components
import EntryImagesMobileFullScreen from './entry-images-mobile-full-screen'
import EntryImagesDesktopSidebar from './entry-images-desktop-sidebar'
// Types
import type {BallotDisplayType} from '../../types/ballot'
// Styles
import styles from './entry-images-modal.module.scss'

interface EntryAdditionalFormField {
  id?: string
  rank?: number | string
  field_label?: string
  type?: string
  value?: any
  hide_field_in_voting_and_gallery_view?: boolean
}

interface BallotFormFieldDropDownChoice {
  rank?: number | string
  name?: string
}

interface BallotAdditionalFormField {
  id?: string
  drop_down_choices?: BallotFormFieldDropDownChoice[]
  hide_field_in_voting_and_gallery_view?: boolean
}

interface EntryWithImages {
  _id?: string
  photo_name?: string
  photo_image?: string
  photo_description?: string
  photo_images_array?: string[]
  cover_image?: string
  additional_form_fields?: EntryAdditionalFormField[]
}

interface VoteObject {
  photoImage?: string
  photoName?: string
  additionalFormFields?: EntryAdditionalFormField[]
}

interface EntryImagesModalProps {
  entry: EntryWithImages
  ballotAdditionalFormFields?: BallotAdditionalFormField[]
  currentWindowHeight: number
  currentWindowWidth: number
  hasVoter: boolean
  onCastVote: (voteObject: VoteObject) => void
  onCloseEntryImagesModal: () => void
  ballotDisplayType?: BallotDisplayType
}

const EntryImagesModal = ({
  entry,
  ballotAdditionalFormFields,
  currentWindowHeight,
  currentWindowWidth,
  hasVoter,
  onCastVote,
  onCloseEntryImagesModal,
  ballotDisplayType,
}: EntryImagesModalProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)

  const paginateImage = (newDirection: number) => {
    const arrayLength = entry?.photo_images_array?.length ?? 0
    if (arrayLength <= 0) return
    const nextImageIndex = currentIndex + newDirection
    let newCurrentImageIndex = nextImageIndex
    if (newDirection > 0) {
      newCurrentImageIndex = (nextImageIndex > (arrayLength - 1)) ? 0 : nextImageIndex
    } else {
      newCurrentImageIndex = (nextImageIndex < 0) ? (arrayLength - 1) : nextImageIndex
    }
    setCurrentIndex(newCurrentImageIndex)
  }

  const [currentGalleryImage, setCurrentGalleryImage] = useState(entry?.cover_image)
  useEffect(() => {
    if (!entry?.photo_images_array?.[currentIndex]) return
    const clonedCurrentGalleryImage = _.clone(entry.photo_images_array[currentIndex])
    const restrictingDimension = currentWindowWidth >= 816 ? currentWindowHeight : currentWindowHeight - 100
    const resizedCurrentGalleryImage = `${clonedCurrentGalleryImage}-/resize/x${restrictingDimension}/-/quality/smart/-/progressive/yes/`
    setCurrentGalleryImage(resizedCurrentGalleryImage)
  }, [currentIndex, entry])

  return entry?.photo_images_array
    ? <>
      {currentWindowWidth < 816 && <>
        <div className={`${styles['entry-images-modal']} ${styles['--is-mobile-full-screen']} ${entry.photo_images_array?.length > 1 ? styles['--has-pagination-controls'] : ''}`}>
          <EntryImagesMobileFullScreen
            entry={entry}
            ballotAdditionalFormFields={ballotAdditionalFormFields}
            currentIndex={currentIndex}
            isShowingPaginationControls={entry.photo_images_array?.length > 1}
            hasVoter={hasVoter}
            onCastVote={onCastVote}
            onCloseEntryImagesModal={onCloseEntryImagesModal}
            ballotDisplayType={ballotDisplayType}
          >
            <div className={styles['__gallery']}>
              <img
                key={currentIndex}
                src={currentGalleryImage}
                // alt=""
                className={styles['__image']}
                style={{maxHeight: (currentWindowWidth >= 816) ? `${currentWindowHeight}px` : `${currentWindowHeight - 100}px`}}
              />
            </div>
            {entry.photo_images_array?.length > 1 &&
              <div className={styles['__below-image-controls']}>
                <button
                  type='button'
                  onClick={() => paginateImage(-1)}
                  className={`${styles['__clickable-area']} ${styles['--previous']}`}
                >
                  <span className={styles['__button']}>{'\u2039 Previous'}</span>
                </button>
                <button
                  type='button'
                  onClick={() => paginateImage(1)}
                  className={`${styles['__clickable-area']} ${styles['--next']}`}
                >
                  <span className={styles['__button']}>{'Next \u203A'}</span>
                </button>
              </div>
            }
          </EntryImagesMobileFullScreen>
        </div>
      </>}

      {currentWindowWidth >= 816 && (
        <EntryImagesDesktopSidebar
          entry={entry}
          ballotAdditionalFormFields={ballotAdditionalFormFields}
          currentIndex={currentIndex}
          hasVoter={hasVoter}
          onCastVote={onCastVote}
          onCloseEntryImagesModal={onCloseEntryImagesModal}
          ballotDisplayType={ballotDisplayType}
        >
          <div className={`${styles['entry-images-modal']} ${styles['--is-desktop-sidebar']} ${entry.photo_images_array?.length > 1 ? styles['--has-pagination-controls'] : ''}`}>
            <div className={styles['__gallery']}>
              <img
                key={currentIndex}
                src={currentGalleryImage}
                // alt=""
                className={styles['__image']}
                style={{
                  maxHeight: (currentWindowWidth >= 816) ? `${currentWindowHeight}px` : `${currentWindowHeight - 100}px`,
                  marginTop: (currentWindowWidth >= 816) ? '0' : '100px',
                }}
              />
            </div>
            {entry.photo_images_array?.length > 1 &&
              <div className={styles['__on-image-controls']}>
                <button
                  type='button'
                  onClick={() => paginateImage(-1)}
                  className={`${styles['__clickable-area']} ${styles['--previous']}`}
                >
                  <span className={styles['__button']}>{'\u2039 Previous'}</span>
                </button>
                <button
                  type='button'
                  onClick={() => paginateImage(1)}
                  className={`${styles['__clickable-area']} ${styles['--next']}`}
                >
                  <span className={styles['__button']}>{'Next \u203A'}</span>
                </button>
              </div>
            }
          </div>
        </EntryImagesDesktopSidebar>
      )}
      <div
        role='button'
        onClick={onCloseEntryImagesModal}
        className={styles['__overlay']}
      ></div>
    </>
    : null
}

export default EntryImagesModal
